import React from 'react';

import { useReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';

import { usePushDevice } from '../../../fetchHooks/usePushDevice';
import useToggleBooleanPush from '../../../fetchHooks/useToggleBooleanPush';
import type { BooleanRule, NotificationType, PushRulesResponse } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { pushTrackIDs } from '../../../utils/gtm/trackIDs/push';
import { ToggleItem } from './ToggleItem';

interface Props {
    notificationType: NotificationType;
    isLoading: boolean;
    notificationRules?: PushRulesResponse;
    // @default true
    enabled?: boolean;
    labelOverride?: string;
}

const BooleanNotificationToggle: React.FC<Props> = ({
    enabled = true,
    notificationType,
    isLoading,
    notificationRules,
    labelOverride,
}) => {
    const { pushEnabledOS } = useReactNativeProps();
    const { data: pushDevice } = usePushDevice(!!pushEnabledOS);
    const { pushNotifications: pushNotificationsTexts } = useTranslation();
    const label = pushNotificationsTexts.toggleLabel[notificationType];
    const rule = notificationRules?.rules.find((rule) => rule.type === notificationType) as BooleanRule | undefined;
    const ruleEnabled = rule?.enabled;
    const togglePush = useToggleBooleanPush(rule);

    if (!enabled) return null;

    return (
        <ToggleItem
            label={labelOverride ?? label}
            subLabel={labelOverride ? label : undefined}
            id={`${notificationType}-toggle`}
            checked={!!pushDevice?.enabled && !!ruleEnabled}
            disabled={!pushDevice?.enabled}
            onToggle={togglePush.mutate}
            aria-label={`${label} bryter`}
            data-track-id={pushTrackIDs.toggle[notificationType]}
            data-track-linkname={`${notificationType}-link`}
            isLoading={isLoading}
        >
            {togglePush.isError && (
                <ErrorLabel text={pushNotificationsTexts.errorToggleRule} icon={IconTypes.UTROPSTEGN_24} />
            )}
        </ToggleItem>
    );
};

export default BooleanNotificationToggle;
